import { EcommerceStatus } from '@/interfaces/ecommerce.interface';
import { ProjectStatus } from '@/modules/cleepo/interfaces/project.interface';
import { useProjectStore } from '@/modules/cleepo/stores/project.store';
import { i18n } from '@/plugins/i18n';
import { useAuthStore } from '@/stores/auth.store';
import { useEcommerceStore } from '@/stores/ecommerce.store';
import { useUiStore } from '@/stores/ui.store';
import { getEffectiveBu } from '@/utils/bu.utils';
import { toast } from '@/utils/toast.utils';
import type { RouteLocationNormalized } from 'vue-router';
import { BusinessUnitKey } from '@/modules/bu/registry.base';

export interface AccountConfigurationStrategy {
  validateAccount: () => Promise<string | undefined>;
  handleNoCurrentOrder: () => Promise<string | undefined>;
  validateIntegration?: () => Promise<string | undefined>;
}

export function getAccountStrategy(
  to_route?: RouteLocationNormalized,
): AccountConfigurationStrategy {
  const authStore = useAuthStore();
  const uiStore = useUiStore();
  const ecommerceStore = useEcommerceStore();
  const projectStore = useProjectStore();
  const { t } = i18n.global as any;

  const flokeeStrategy: AccountConfigurationStrategy = {
    validateAccount: async () => {
      const hasAccounts = authStore.user?.accounts && authStore.user?.accounts?.length > 0;
      if (!authStore.user?.current_account_id) {
        if (authStore.user?.role.id === 1 || authStore.user?.role.id === 3) {
          return 'AdminSelectAccountView';
        }
        return hasAccounts ? 'SelectAccountView' : 'WizardCreateAccountView';
      }

      if (!authStore.currentAccount?.active) {
        return hasAccounts ? 'SelectAccountView' : 'WizardCreateAccountView';
      }
    },
    handleNoCurrentOrder: async () => {
      if (authStore.user?.role.id === 1 || authStore.user?.role.id === 3) {
        return 'AdminSelectAccountView';
      }

      const hasEcommerces = ecommerceStore.ecommerces.length > 0;
      if (!hasEcommerces) {
        return 'WizardCreateEcommerceView';
      }

      const activeEcommerce = ecommerceStore.ecommerces.find(
        (e) => e.status === EcommerceStatus.ACTIVE,
      );

      try {
        if (activeEcommerce) {
          await ecommerceStore.setSelectedEcommerce(activeEcommerce.id);
        } else {
          await ecommerceStore.setSelectedEcommerce(ecommerceStore.ecommerces[0].id);
        }
      } catch (error) {
        toast.error({ message: t('notifications.account_not_configured_error') });
        uiStore.openSupportDrawer();
        return 'AuthLoginView';
      }
    },
    validateIntegration: async () => {
      if (ecommerceStore.currentEcommerce && !ecommerceStore.currentEcommerce?.integration) {
        if (authStore.user?.role.id === 1 || authStore.user?.role.id === 3) {
          toast.error({ message: t('notifications.ecommerce_integration_not_set_error') });
          return 'AdminSelectAccountView';
        }
        return 'WizardSelectEcommerceProviderView';
      }
    },
  };

  const cleepoStrategy: AccountConfigurationStrategy = {
    validateAccount: async () => {
      const hasAccounts = authStore.user?.accounts && authStore.user?.accounts?.length > 0;
      if (!authStore.user?.current_account_id) {
        if (authStore.user?.role.id === 1 || authStore.user?.role.id === 3) {
          return 'AdminSelectAccountView';
        }
        return hasAccounts ? 'CleepoSelectAccountView' : 'CleepoWizardCreateAccountView';
      }

      if (!authStore.currentAccount?.active) {
        return hasAccounts ? 'CleepoSelectAccountView' : 'CleepoWizardCreateAccountView';
      }
    },
    handleNoCurrentOrder: async () => {
      if (authStore.user?.role.id === 1 || authStore.user?.role.id === 3) {
        return 'AdminSelectAccountView';
      }

      const hasProjects = projectStore.projects.length > 0;
      if (!hasProjects) {
        //TODO: if user has no projects, create the project.
        return 'CleepoWizardCreateAccountView';
      }

      const activeProject = projectStore.projects.find((p) => p.status === ProjectStatus.ACTIVE);

      try {
        if (activeProject) {
          await projectStore.setSelectedProject(activeProject.id);
        } else {
          await projectStore.setSelectedProject(projectStore.projects[0].id);
        }
      } catch (error) {
        toast.error({ message: t('notifications.account_not_configured_error') });
        uiStore.openSupportDrawer();
        return 'CleepoAuthLoginView';
      }
    },
  };

  const path_value = to_route?.path ?? window.location.pathname;
  const resolved_bu = getEffectiveBu({
    path_value,
    origin_value: window.location.origin,
    use_stored: true,
  });

  const strategyByBu: Record<BusinessUnitKey, AccountConfigurationStrategy> = {
    [BusinessUnitKey.Cleepo]: cleepoStrategy,
    [BusinessUnitKey.Flokee]: flokeeStrategy,
  };

  if (resolved_bu) return strategyByBu[resolved_bu];

  return authStore.isCleepoAccount ? cleepoStrategy : flokeeStrategy;
}
